import React from "react";
import { FAMILY_ALL } from "../../../lib/productFamily";

// Product Family navigation for the customer Sales Order (Sep 2026).
//
// Sits ABOVE the existing category row — it does not replace it — so
// the hierarchy the customer reads down the page is:
//
//     Search  ->  Product Family  ->  Category  ->  Products
//
// Visual language is deliberately the DARK header band rather than the
// category row's own pale band, so the two rows are obviously
// different levels of the same navigation instead of two rows of
// identical pills. Selection styling reuses the category row's orange
// so "this is the one you picked" means the same thing on both rows.
//
// Responsive behaviour copies `CategoryTabs` exactly — one horizontally
// scrollable line of full-size touch targets. Labels are never shrunk
// to fit: on a phone the row scrolls, on a Samsung tablet/kiosk it
// does not need to. `shrink-0` on the nav keeps it out of the
// scrolling menu area, so it can never overlap the category row or the
// search bar however narrow the viewport gets.
export default function ProductFamilyTabs({ tabs, active, onChange }) {
  // One family (or none) is not a choice — rendering a row the customer
  // cannot use would add a level of navigation for nothing. Every
  // catalog that has not classified its products therefore renders
  // exactly what it renders today: no family row at all.
  if (!tabs || tabs.length < 2) return null;
  const activeKey = active || FAMILY_ALL;
  return (
    <nav
      className="bg-[#0a1a0e] border-b border-emerald-900/40 px-3 sm:px-4 py-2 overflow-x-auto scrollbar-none shrink-0"
      aria-label="Product family"
      data-testid="retail-family-tabs"
    >
      {/* Right-aligned: these are catalog-level Product Family
          selectors, a different level from the category row beneath
          them, and pushing them to the far right is what tells the
          customer that at a glance. `w-full` gives the row something to
          justify against while `min-w-max` still lets it overflow and
          scroll horizontally on a narrow screen rather than squeezing
          the labels. */}
      <div className="flex items-center gap-2 w-full min-w-max justify-end" role="tablist">
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(tab.key)}
              data-testid={`retail-family-${tab.key === FAMILY_ALL ? "all" : tab.key.replace(/\s+/g, "-")}`}
              className={
                "shrink-0 h-10 px-4 rounded-md text-sm font-semibold uppercase tracking-wide transition " +
                (isActive
                  ? "bg-orange-500 text-white"
                  : "bg-emerald-900/40 text-emerald-100 border border-emerald-300/20 hover:bg-emerald-900/70")
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
